import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {firstValueFrom, map, Subject} from "rxjs";
// @ts-ignore
import * as hclParser from 'js-hcl-parser';
// @ts-ignore
import * as yaml from 'js-yaml';
import {Utils} from "../../../core/helpers/Utils";
import {Const, ResourceState} from "../../../core/helpers/Const";
import {FetchResponse, PollingService} from "../../../service/services/misc/polling";
import {OutputVariable} from '../models/OutputVariable.model';

export interface SolutionEndpoint {
    displayName: string;
    type: string;
    url: string;
}

@Injectable({
    providedIn: 'root',
})
export class SolutionService {
    solutionChanged$ = new Subject<void>();

    constructor(private http: HttpClient,
                private pollingService: PollingService) {
    }

    activeEntity: any = undefined;
    solutionType = "sap-managed";
    solutionReadiness = {
        solutionReadyStatus: undefined as ResourceState,
        solutionReadyStatusTooltip: undefined as string,
        solutionLastModifiedDate: undefined as string,
    };
    modulesMetadata: any = undefined;
    emptySolutionStatus = false;
    solutionOutputs: OutputVariable[] = [];
    solutionOutputMetadata: any = undefined;
    keysNotToBeRemoved = ["source_system_transformations", "target_system_transformations", "gtid", "spcSystemId",
        "spcExternalTenantId", "applicationName", "saas_app_name", "saas_plan_name", "service_name", "service_plan_name", "service_instance_name",
        "service_offering_name", "role_collection_name", "btpAppName", "btpConsumerTenantId"];
    deletedModules: any = {};
    MAX_POLLING_NO_STATUS = 60;
    readOnlyLocals = {
        'customer-managed': ['automation_context', 'subaccount_credentials' ,'users'],
        'sap-managed': ['automation_context', 'subaccount_credentials' ,'users','saml_metadata_location','ias_tenant_host','subaccount_display_name', 'cloudfoundry_landscape','configuration_url','subaccount_admins','tenant_context','initial_user','customer_contact']
    };
    reservedSolutionOutputNames = ['subscriptionUrl', 'subaccountId', 'externalId', 'subscription_url', 'gtid', 'spcSystemId',
        'spcExternalTenantId', 'applicationName', 'btpAppName', 'btpConsumerTenantId', 'endpoints'];
    private pollingActive = false;

    async setSolutionType(type: string) {
        this.solutionType = type;
        await this.initModulesMetadata();
        for (const entity of Object.keys(this.buildingBlocksDefinition)) {
            if (this.buildingBlocksDefinition[entity].module && this.buildingBlocksDefinition[entity].module.startsWith("-")) {
                this.buildingBlocksDefinition[entity].module = this.solutionType + this.buildingBlocksDefinition[entity].module;
                const extractedData = this.extractVariablesAndOutputs(this.buildingBlocksDefinition[entity].module);
                this.buildingBlocksDefinition[entity].variables = extractedData.variables;
                this.buildingBlocksDefinition[entity].outputs = extractedData.outputs
                    .filter((output: any) => output.name !== 'cis_local_credentials' && output.name !== 'cis_service_management_binding');
                this.buildingBlocksDefinition[entity].providers = extractedData.providers;
            }
        }
    }

    async initModulesMetadata() {
        if (this.modulesMetadata === undefined) {
            this.modulesMetadata = await firstValueFrom(this.http.get('/assets/cloud-automation-designer/modules-metadata.json', {responseType: 'json'}));
        }
        if (this.solutionOutputMetadata === undefined) {
            this.solutionOutputMetadata = await firstValueFrom(this.http.get('/assets/cloud-automation-designer/solution-output-metadata.json', {responseType: 'json'}));
        }
    }

    buildingBlocksDefinition = this.initBuildingBlocksDefinition();
    initBuildingBlocksDefinition():any {
        return {
            "subaccount": {
                name: "BTP\nSubaccount",
                shortName: "Subaccount",
                technicalName: '-cis-subaccount',
                icon: "/assets/cloud-automation-designer/icons/subaccount.svg",
                module: '-cis-subaccount',
                provider: '/assets/cloud-automation-designer/templates/providers/cis.json',
                source: 'http://api.cloud-automation-registry/modules/cis-subaccount',
                outputs: [{name: 'status', type: 'string'}, {name: 'id', type: 'string'}],
                variables: [],
                providers: [],
                expanded: true,
                supportsPatchByDeletion: false
            },
            "lean_subaccount": {
                name: "BTP\nSubaccount",
                shortName: "Subaccount",
                technicalName: '-lean-subaccount',
                icon: "/assets/cloud-automation-designer/icons/subaccount.svg",
                module: '-lean-subaccount',
                provider: '/assets/cloud-automation-designer/templates/providers/cis.json',
                source: 'http://api.cloud-automation-registry/modules/lean-subaccount',
                outputs: [],
                variables: [],
                providers: [],
                expanded: true,
                supportsPatchByDeletion: false
            },
            "lightweight_subaccount": {
                name: "BTP\nSubaccount",
                shortName: "Subaccount",
                technicalName: '-lightweight-subaccount',
                icon: "/assets/cloud-automation-designer/icons/subaccount.svg",
                module: '-lightweight-subaccount',
                provider: '/assets/cloud-automation-designer/templates/providers/cis.json',
                source: 'http://api.cloud-automation-registry/modules/lightweight-subaccount',
                outputs: [],
                variables: [],
                providers: [],
                expanded: true,
                supportsPatchByDeletion: false
            },
            "shadow_users": {
                module: '-shadow-users',
                name: "Shadow\nUsers",
                shortName: "Shadow Users",
                icon: "/assets/cloud-automation-designer/icons/shadow-users.svg",
                technicalName: '-shadow-users',
                provider: '/assets/cloud-automation-designer/templates/providers/xsuaa.json',
                source: 'http://api.cloud-automation-registry/modules/shadow-users',
                outputs: [],
                expanded: true,
                variables: [],
                providers: [],
                supportsPatchByDeletion: true
            },
            "trust_setup": {
                provider: '/assets/cloud-automation-designer/templates/providers/xsuaa.json',
                module: '-trust-setup',
                name: "Trust\nSetup",
                shortName: "Trust Setup",
                technicalName: "-trust-setup",
                icon: "/assets/cloud-automation-designer/icons/trust.svg",
                source: 'http://api.cloud-automation-registry/modules/xsuaa-trust-setup',
                outputs: [],
                variables: [],
                providers: [],
                expanded: true,
                supportsPatchByDeletion: true
            },
            "security_settings": {
                provider: '/assets/cloud-automation-designer/templates/providers/xsuaa.json',
                module: '-security-settings',
                name: "Security\nSettings",
                shortName: "Security Settings",
                technicalName: "-security-settings",
                icon: "/assets/cloud-automation-designer/icons/validate.svg",
                source: 'http://api.cloud-automation-registry/modules/security-settings',
                outputs: [],
                variables: [],
                providers: [],
                expanded: true,
                supportsPatchByDeletion: false
            },
            "assignment": {
                name: "Entitlement\nAssignment",
                shortName: "Assignment",
                technicalName: '-entitlements',
                icon: "/assets/cloud-automation-designer/icons/entitlement.svg",
                module: '-entitlements',
                provider: '/assets/cloud-automation-designer/templates/providers/cis.json',
                source: 'http://api.cloud-automation-registry/modules/cis-entitlements',
                outputs: [],
                variables: [],
                providers: [],
                expanded: true,
                supportsPatchByDeletion: true
            },
            "subscription": {
                name: "SaaS\nSubscriptions",
                shortName: "Subscription",
                technicalName: "-subscription",
                icon: "/assets/cloud-automation-designer/icons/subscription.svg",
                module: '-subscription',
                provider: '/assets/cloud-automation-designer/templates/providers/cis.json',
                source: 'http://api.cloud-automation-registry/modules/cis-subscriptions',
                outputs: [],
                variables: [],
                providers: [],
                expanded: true,
                solutionOutput: {
                    "sap-managed": [
                        {
                            endpointOutput: true,
                            displayName: "Main Application URL",
                            type: "application",
                            moduleOutputName: "subscription_url"
                        },
                        {
                            moduleOutputName: "subscription_guid",
                            outputName: "subscriptionId",
                            outputDescription: "The subscription ID"
                        },
                    ],
                    "customer-managed": [
                        {
                            moduleOutputName: "subscription_url",
                            outputName: "subscription_url",
                            outputDescription: "The subscription URL"
                        },
                        {
                            moduleOutputName: "subscription_app_name",
                            outputName: "subscription_app_name",
                            outputDescription: "The subscription app name"
                        }
                    ]
                },
                supportsPatchByDeletion: true
            },
            "environment": {
                name: "Environment\nInstances",
                shortName: "Environment",
                technicalName: "-environment-cf",
                icon: "/assets/cloud-automation-designer/icons/environment.svg",
                provider: '/assets/cloud-automation-designer/templates/providers/cis.json',
                module: '-environment-cf',
                source: 'http://api.cloud-automation-registry/modules/cis-environment-instance-cf',
                outputs: [],
                variables: [],
                providers: [],
                expanded: true,
                supportsPatchByDeletion: true
            },
            "service": {
                name: "Service\nInstances",
                shortName: "Service",
                technicalName: "-service",
                icon: "/assets/cloud-automation-designer/icons/service.svg",
                module: '-service',
                provider: '/assets/cloud-automation-designer/templates/providers/sm.json',
                source: "http://api.cloud-automation-registry/modules/sm-service-instance-and-binding",
                outputs: [{"name": "service_binding", "type": "map"}, {"name": "service_instance", "type": "map"}],
                variables: [],
                providers: [],
                expanded: true,
                supportsPatchByDeletion: true
            },
            "destination": {
                name: "Destination\nConfigurations",
                shortName: "Destination",
                technicalName: "-destination-configuration",
                icon: "/assets/cloud-automation-designer/icons/destination.svg",
                module: '-destination-configuration',
                provider: '/assets/cloud-automation-designer/templates/providers/destination.json',
                source: 'http://api.cloud-automation-registry/modules/destination-configuration',
                outputs: [{"name": "IdKey", "type": "string"}],
                variables: [],
                providers: [],
                variableTemplates:
                    {
                        "certificate_configuration":
                        {
                            "Name": "cert-${local.subaccount_credentials.subaccount_guid}-UUIDGEN.pem",
                            "Attributes": {
                                "AutomaticRenew": true,
                            }
                        }
                    },
                expanded: true,
                supportsPatchByDeletion: true
            },
            "role_collection": {
                name: "Role\nCollection",
                shortName: "Role Collection",
                technicalName: '-role-collection',
                icon: "/assets/cloud-automation-designer/icons/entitlement.svg",
                module: '-role-collection',
                provider: '/assets/cloud-automation-designer/templates/providers/xsuaa.json',
                source: 'http://api.cloud-automation-registry/modules/xsuaa-role-collection-assignment',
                outputs: [],
                variables: [],
                providers: [],
                expanded: true,
                supportsPatchByDeletion: true
            },
            "ips_configuration": {
                name: "IPS\nConfiguration",
                shortName: "IPS Configuration",
                technicalName: '-ips-configuration',
                icon: "/assets/cloud-automation-designer/icons/trust.svg",
                module: '-ips-configuration',
                provider: '/assets/cloud-automation-designer/templates/providers/xsuaa.json',
                source: 'http://api.cloud-automation-registry/modules/xsuaa-role-collection-assignment',
                outputs: [],
                variables: [],
                providers: [],
                variableTemplates: {
                    "target_system_custom_properties": {
                        "key1": "value1",
                        "key2": "value2"
                    },
                    "source_system_custom_properties": {
                        "key1": "value1",
                        "key2": "value2"
                    }
                },
                expanded: true,
                supportsPatchByDeletion: false
            },
            "ias_user_group": {
                name: "IAS\nUser\nGroup",
                shortName: "IAS User Group",
                technicalName: '-ias-user-group',
                icon: "/assets/cloud-automation-designer/icons/ias.svg",
                module: '-ias-user-group',
                provider: '/assets/cloud-automation-designer/templates/providers/xsuaa.json',
                source: 'http://api.cloud-automation-registry/modules/ias-user-group',
                outputs: [],
                variables: [],
                providers: [],
                expanded: true,
                supportsPatchByDeletion: false
            },
            "ias_app_specific_group": {
                name: "IAS App\nSpecific\nGroup",
                shortName: "IAS App Specific Group",
                technicalName: '-ias-app-specific-user-group',
                icon: "/assets/cloud-automation-designer/icons/ias.svg",
                module: '-ias-app-specific-user-group',
                provider: '/assets/cloud-automation-designer/templates/providers/xsuaa.json',
                source: 'http://api.cloud-automation-registry/modules/ias-app-specific-user-group',
                outputs: [],
                variables: [],
                providers: [],
                expanded: true,
                supportsPatchByDeletion: false
            },
            "ias_technical_user":
                {
                    provider: '/assets/cloud-automation-designer/templates/providers/xsuaa.json',
                    source: 'http://api.cloud-automation-registry/modules/ias_technical_user',
                    outputs: [],
                    technicalName: '-ias-technical-user',
                    variables: [],
                    providers: [],
                    expanded: true,
                    name: "IAS\nTechnical\nUser",
                    shortName: "IAS Technical User",
                    icon: "/assets/cloud-automation-designer/icons/ias.svg",
                    module: '-ias-technical-user',
                    supportsPatchByDeletion: false
                },
            "api_request": {
                outputs: [],
                technicalName: '-api-request',
                variables: [],
                expanded: true,
                name: "API\nRequest",
                shortName: "API Request",
                icon: "/assets/cloud-automation-designer/icons/api.svg",
                module: '-api-request',
                supportsPatchByDeletion: false
            },
            "api_request_status_polling": {
                outputs: [],
                technicalName: '-api-request-status-polling',
                variables: [],
                expanded: true,
                name: "API\nStatus Polling",
                shortName: "API Status Polling",
                icon: "/assets/cloud-automation-designer/icons/api.svg",
                module: '-api-request-status-polling',
                supportsPatchByDeletion: false
            }
        };
    }


    async initSolution() {
        this.initDeletedModules(this.getPatchByDeletionSolution());
        const solutionStr = localStorage.getItem("solution");
        if (solutionStr){
            await this.setSolutionType(solutionStr.includes("customer-managed") ? "customer-managed" : "sap-managed");
        }
    }

    getSolution(): any {
        const solutionStr = localStorage.getItem("solution");
        if (solutionStr !== null) {
            return JSON.parse(solutionStr);
        }

        return null;
    }

    removePatchByDeletionSolution() {
        this.deletedModules = {};
        localStorage.removeItem("patch_by_deletion_solution");
    }

    getPatchByDeletionSolution(): any {
        const solutionStr = localStorage.getItem("patch_by_deletion_solution");
        if (solutionStr !== null) {
            return JSON.parse(solutionStr);
        }

        return null;
    }

    setSolution(solution: any) {
        localStorage.setItem("solution", JSON.stringify(solution));
        // Trigger a 'solutionChanged' event to notify its subscribers
        this.solutionChanged$.next();
    }

    setPatchByDeletionSolution(patchByDeletionSolution: any) {
        this.initDeletedModules(patchByDeletionSolution);

        if (Object.keys(this.deletedModules).length === 0) {
            this.removePatchByDeletionSolution();
        } else {
            localStorage.setItem("patch_by_deletion_solution", JSON.stringify(patchByDeletionSolution));
        }
    }

    private initDeletedModules(patchByDeletionSolution: any) {
        this.deletedModules = {};
        if (patchByDeletionSolution?.content?.['main.tf']?.module) {
            const modules = patchByDeletionSolution.content['main.tf'].module;

            Object.keys(modules).forEach((name: string) => {
                if (!modules[name].hasOwnProperty("read_only_mode")) {
                    this.deletedModules[name] = modules[name];
                }
            });
        }
    }

    isReadonlyModule(moduleName: string): boolean {
        const solution = this.getSolution();
        if (solution?.content?.['main.tf']?.module?.[moduleName]) {
            return solution.content['main.tf'].module[moduleName].read_only_mode === true;
        }
        return false;
    }

    clearAll() {
        localStorage.clear();
        this.activeEntity = undefined;
        this.buildingBlocksDefinition = this.initBuildingBlocksDefinition();
    }

    nameCounter: { [key: string]: number } = {};

    generateUniqueName(objName: string) {
    // Initialize the counter for the given object name if it doesn't exist
        if (!this.nameCounter[objName]) {
            this.nameCounter[objName] = 1;
        }

        // Generate the unique name by appending the current counter value
        const uniqueName = `${objName}-${this.nameCounter[objName]}`;

        // Increment the counter for the next use
        this.nameCounter[objName]++;

        return uniqueName;
    }

    deleteName(fullName: string) {
        const [objName, indexStr] = fullName.split('-');
        const index = parseInt(indexStr);

        if (this.nameCounter[objName] && this.nameCounter[objName] >= index) {
            for (let i = index; i < this.nameCounter[objName]; i++) {
                const currentName = `${objName}-${i + 1}`;
                const nextName = `${objName}-${i}`;
                if (!this.nameCounter[currentName]) {
                    break;
                }
                this.nameCounter[nextName] = this.nameCounter[currentName];
                delete this.nameCounter[currentName];
            }
            this.nameCounter[objName]--;
        }
    }


    addBuildingBlockIfMissing(buildingBlock: string, name: string) {
        if (this.getBuildingBlocksBySource(this.buildingBlocksDefinition[buildingBlock].module).length === 0) {
            this.addBuildingBlock(buildingBlock, name);
        }
    }
    async addBuildingBlock(buildingBlock: string, name: string) {
        let newName = this.generateUniqueName(name);
        await this.addModule(newName, this.buildingBlocksDefinition[buildingBlock]);
        return newName;
    }

    getBuildingBlocksBySource(source: string): string[] {
        return this.getBuildingBlocksBySourceAndSolution(source, this.getSolution());
    }

    getBuildingBlocksBySourceAndSolution(source: string, solution: any): string[] {
        if (!solution || !solution.content['main.tf'] || !source) {
            return [];
        }

        source = source.substring(source.lastIndexOf('/') + 1);
        const result: string[] = [];
        if(solution.content['main.tf'] != null && solution.content['main.tf'].module != null) {
            const modules = solution.content['main.tf'].module;
            Object.keys(modules).forEach((name: string) => {

                const moduleSource = modules[name].source.substring(modules[name].source.lastIndexOf('/') + 1);
                if (moduleSource === source) {
                    result.push(name);
                }
            });
        }

        return result;
    }

    getBuildingBlocksDefinitionBySource(source: string): any {
        source = source.substring(source.lastIndexOf('/') + 1);
        for (const name of Object.keys(this.buildingBlocksDefinition)) {
            if (this.buildingBlocksDefinition[name].module === source) {
                return this.buildingBlocksDefinition[name];
            }
        }
        return null;
    }

    getBuildingBlocksBySourceExclude(source: string, exclude: string): string[] {
        return  this.getBuildingBlocksBySource(source).filter((bb: string) => bb !== exclude);
    }

    editBuildingBlockName(currentName: string, newName: string) {
        // Rename the module in the solution
        let solution = this.getSolution();
        let isNameExist = this.checkIfModuleNameAlreadyExists(solution, newName);
        if (isNameExist) {
            throw new Error("Module named '" + newName + "' already exists.");
        }

        this.renameModuleInSolution(solution, currentName, newName);
        this.activeEntity.resource = newName;
        this.setSolution(solution);

        // Rename the module in patchByDeletionSolution
        let patchByDeletionSolution = this.getPatchByDeletionSolution();
        if (patchByDeletionSolution) {
            this.renameModuleInSolution(patchByDeletionSolution, currentName, newName, true);
            this.setPatchByDeletionSolution(patchByDeletionSolution);
        }
    }

    checkIfModuleNameAlreadyExists(solution: any, name: string) {
        const modules = solution.content['main.tf'].module;
        return Object.keys(modules).filter(key => key === name).length > 0;
    }

    renameModuleInSolution(solution: any, currentName: string, newName: string, renameOnlyReadonlyModules=false) {
        const modules = solution.content['main.tf'].module;

        if (renameOnlyReadonlyModules && modules[currentName] && !modules[currentName].hasOwnProperty("read_only_mode")) {
            // Do not rename the module if it is not in read-only mode
            return;
        }

        solution.content['main.tf'].module = Object.keys(modules).reduce(
            (s, name) =>
                name === currentName ? ({...s, [newName]: modules[currentName]}) : ({...s, [name]: modules[name]}),
            {});

        Object.keys(solution.content['main.tf'].module).forEach((name: string) => {
            const moduleRef = "${module." + currentName + "}";
            const newNameRef = "${module." + newName + "}";

            if (solution.content['main.tf'].module[name].dependencies && Array.isArray(solution.content['main.tf'].module[name].dependencies)) {
                solution.content['main.tf'].module[name].dependencies = solution.content['main.tf'].module[name].dependencies.map((dep: string) => dep.replace(moduleRef, newNameRef));
            }

            Object.keys(solution.content['main.tf'].module[name]).forEach((moduleProperty: string) => {
                const propertyValue = solution.content['main.tf'].module[name][moduleProperty];
                if (typeof propertyValue === 'string') {
                    solution.content['main.tf'].module[name][moduleProperty] = propertyValue.replace(/\$\{.*?module\.([^.]+)\./, (match: string, moduleName: string) => {
                        if (moduleName === currentName) {
                            return match.replace(moduleName, newName);
                        }
                        return match; // Return unchanged if the module name doesn't match
                    });
                }
            });

        });

        if (solution.content['main.tf'].output) {
            const modulePattern = "${module." + currentName + ".";
            const replacement = "${module." + newName + ".";

            Object.keys(solution.content['main.tf'].output).forEach((output: string) => {
                const value = solution.content['main.tf'].output[output].value;
                if (typeof value === 'string') {
                    solution.content['main.tf'].output[output].value = value.replace(modulePattern, replacement);
                }
            });

            // Update module references in endpoints array
            const endpoints = solution.content['main.tf'].output?.endpoints?.value;
            if (Array.isArray(endpoints)) {
                endpoints.forEach((endpoint: any) => {
                    if (typeof endpoint['url'] === 'string') {
                        endpoint['url'] = endpoint['url'].replace(modulePattern, replacement);
                    }
                });
            }
        }

        if (solution.content['main.tf']['locals'] && solution.content['main.tf']['locals'][0]) {
            solution.content['main.tf']['locals'][0] = this.updateModuleOccurrences(solution.content['main.tf']['locals'][0], currentName, newName);
        }
    }

    updateModuleOccurrences(obj: any, oldModuleName: string, newModuleName: string): any {
        if (typeof obj !== 'object') {
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj.map((item) => this.updateModuleOccurrences(item, oldModuleName, newModuleName));
        }

        return Object.keys(obj).reduce((acc, key) => {
            const value = obj[key];

            if (typeof value === 'string') {
                acc[key] = value.replace(new RegExp("module\\."+oldModuleName+"\\.", 'g'), "module."+newModuleName+".");
            } else {
                acc[key] = this.updateModuleOccurrences(value, oldModuleName, newModuleName);
            }

            return acc;
        }, {} as any);
    }

    toSnakeCase(str: string): string {
        return str === undefined || str === "" ? "" : str
            .match(/[A-Z]{2,}(?=[A-Z][a-z]+\d*|\b)|[A-Z]?[a-z]+\d*|[A-Z]|\d+/g) //NOSONAR
            .join('_')
            .toLowerCase();
    }

    handleJsonValue(variableName: string, newValue: string) {
        if (newValue != "" && !newValue.startsWith("${") && (newValue.startsWith("{") || newValue.startsWith("["))) {
            try {
                newValue = JSON.parse(newValue);
                if (!Array.isArray(newValue) && variableName != 'labels' && variableName != 'certificate_configuration') {
                    newValue = this.wrapInnerJsonWithPrefix(newValue);
                }
                newValue = "${" + JSON.stringify(newValue) + "}";
            } catch (e) {
                console.error("Invalid json:", newValue, e);
            }
        }
        return newValue;
    }

    displayJsonValue(variableName: string, value: string) {
        if (value != "" && value.startsWith("{") && variableName != 'labels' && variableName != 'certificate_configuration') {
            try {
                value = JSON.parse(value);
                value = this.unwrapInnerJsonFromPrefix(value);
                value = JSON.stringify(value);
            } catch (e) {
            }
        }
        return value;
    }

    wrapInnerJsonWithPrefix(jsonValue: any) {
        Object.keys(jsonValue).forEach((key: string) => {
            if (typeof jsonValue[key] === 'object') {
                jsonValue[key] = ".json:" + JSON.stringify(jsonValue[key]);
            }
        });
        return jsonValue;
    }

    unwrapInnerJsonFromPrefix(jsonValue: any) {
        Object.keys(jsonValue).forEach((key: string) => {
            let value = jsonValue[key];
            if (typeof value === 'string' && value.startsWith('.json:')) {
                value = value.replace('.json:', '');
                jsonValue[key] = JSON.parse(value);
            }
        });

        return jsonValue;
    }

    updateVariableValue(buildingBlockName: any, variableName: string, event: any) {
        let newValue: string;
        if (event.target) {
            newValue = event.target.value;
            newValue = `${newValue}`;
        } else {
            newValue = event;
        }

        if (typeof newValue === "string") {
            newValue = newValue.trim();
            // Remove &nbsp; chars which corrupt the yaml
            newValue = newValue.replace(/\u00A0/g, '');
        }

        const solution = this.getSolution();

        if (buildingBlockName === Const.SOLUTION_OUTPUTS_BUILDING_BLOCK_NAME) {
            if (!solution.content['main.tf'].output[variableName]) {
                return;
            }
            solution.content['main.tf'].output[variableName].value = this.handleJsonValue(variableName, newValue);
        } else {
            if (!solution.content['main.tf'].module[buildingBlockName]) {
                return;
            }
            solution.content['main.tf'].module[buildingBlockName][this.toSnakeCase(variableName)] = this.handleJsonValue(variableName, newValue);

            if (variableName === 'cloudfoundry' && newValue === "true" && this.solutionType === "sap-managed") {
                solution.content['main.tf'].module[buildingBlockName]['cloudfoundry_landscape'] = "cf-${local.automation_context.subaccount_region}";
            }
        }

        this.setSolution(solution);
    }

    getVariableValue(buildingBlockName: string, variableName: string): string {
        const solution = this.getSolution();
        return buildingBlockName === Const.SOLUTION_OUTPUTS_BUILDING_BLOCK_NAME
            ? this.getVariableValueFromOutput(solution, variableName)
            : this.getVariableValueFromModule(solution, buildingBlockName, variableName);
    }

    getVariableValueFromOutput(solution: any, variableName: string): string{
        const outputs = solution.content?.['main.tf']?.output;
        return outputs?.[variableName]?.value || "";
    }

    getVariableValueFromModule(solution: any, buildingBlockName: string, variableName: string): string {
        const modules = solution.content?.['main.tf']?.module;
        return modules?.[buildingBlockName]?.[variableName] || "";
    }

    isBound(entity: any, variableName: any): boolean {
        const value = (entity == Const.SOLUTION_OUTPUTS_BUILDING_BLOCK_NAME) ? this.getVariableValue(entity, variableName) : this.getVariableValue(entity.resource, variableName);
        return this.isValueBound(value);
    }

    isValueBound(value: any): boolean {
        return typeof value === "string" && /^\${(var|module|local)\..*}$/.test(value) && !value.includes("?");
    }

    async addModule(moduleName: string, buildingBlock: any) {
        const solution = this.getSolution();
        const module: any = { "source":  "http://api.cloud-automation-registry/modules/" + buildingBlock.module };
        const sapManagedAdditions: any = this.solutionType === "sap-managed" ? {
            "subaccount_display_name": "${var.tenantContext.details.customer.name}: ${replace(var.tenantContext.subject.id, \"$\", \"\")",
            "subaccount_identifier": "${local.automation_context.subaccount_subdomain}",
            "subaccount_admins": "${local.subaccount_admins}",
            "users": "${local.users}",
            "space_auditors": "${local.users}",
            "subaccount_region": "${var.tenantContext.details.tenant.spec.btpRegion}",
            "ias_tenant_host": "${local.ias_tenant_host}",
            "org_name": "${replace(var.tenantContext.subject.id, \"$\", \"\")}",
            "user_email": "${local.subaccount_admins[0]}",
            "customer_id": "${var.tenantContext.details.customer.id}"

        } : {
            "users": "${local.users}",
            "space_managers": "${local.users}"
        };

        let addInputs = [{"name": "automation_context"}];
        if (buildingBlock.shortName !== "Assignment") {
            addInputs.push({"name": "subaccount_credentials"});
        }

        if (buildingBlock.shortName === "Destination") {
            addInputs = [];
        }

        if (buildingBlock.shortName === "Trust Setup" && this.solutionType === "sap-managed") {
            if (!solution.content["main.tf"].locals[0].hasOwnProperty("ias_tenant_host")) {
                solution.content["main.tf"].locals[0]["ias_tenant_host"] = '${try(trimprefix(var.tenantContext.details.tenant.spec.iasUrl, "https://"), "")}';
            }
        }

        const variables = [...buildingBlock.variables, ...addInputs];

        variables.forEach((variable: any) => {
            if (variable.name === "subaccount_guid" && !buildingBlock.module.includes("subaccount")) {
                const subaccountModuleName = this.getBuildingBlocksBySource(this.buildingBlocksDefinition.subaccount.module)[0];
                module[variable.name] = "${module." + subaccountModuleName + ".subaccount_guid}";
            } else if (Object.keys(sapManagedAdditions).includes(variable.name)) {
                module[variable.name] = sapManagedAdditions[variable.name];
            } else if (solution.content['variables.tf']!= null && Object.keys(solution.content['variables.tf'].variable).includes(variable.name)) {
                module[variable.name] = '${var.' + variable.name + '}';
            } else if (solution.content['main.tf'] != null && solution.content['main.tf']['locals'] != null &&
                solution.content['main.tf']['locals'].length > 0 && Object.keys(solution.content['main.tf']['locals'][0]).includes(variable.name) &&
                !['initial_user', 'customer_contact'].includes(variable.name)) {
                module[variable.name] = '${local.' + variable.name + '}';
            } else {
                module[variable.name] = "";
            }
        });

        if (buildingBlock.shortName === "IPS Configuration") {
            module['ias_tenant_host'] = "";
            module['account_identifier'] = "${{\"id\":\"0123456789\",\"type\":\"CRM / COST_CENTER\"}}";
        }

        module['dependencies'] = [];

        this.addModuleOutputsToSolution(solution, moduleName, buildingBlock);

        solution.content['main.tf'].module[moduleName] = module;

        const hasSubaccountModule = this.getBuildingBlocksBySource(this.buildingBlocksDefinition.subaccount.module).length > 0;
        if (!hasSubaccountModule && buildingBlock.module != this.buildingBlocksDefinition.api_request.module && buildingBlock.module != this.buildingBlocksDefinition.api_request_status_polling.module) {
            const path = '/assets/cloud-automation-designer/templates/solutions/' + this.solutionType + '/blank-template.yaml';
            let blankTemplateContent: string = await firstValueFrom(this.http.get(path, { responseType: 'text' }).pipe(
                map((yamlData: string) => {
                    return yamlData;
                })
            ));

            let blankSolution: any = this.loadSolution(yaml.load(blankTemplateContent), false, false);
            solution.content['main.tf'].module = { ...solution.content['main.tf'].module, ...blankSolution.content['main.tf'].module };
            if (solution.content['main.tf'].locals && solution.content['main.tf'].locals.length > 0) {
                solution.content['main.tf'].locals[0] = { ...solution.content['main.tf'].locals[0], ...blankSolution.content['main.tf'].locals[0] };
            } else {
                solution.content['main.tf'].locals = blankSolution.content['main.tf'].locals;
            }

            if (solution.content['main.tf'].outputs && solution.content['main.tf'].outputs.length > 0) {
                solution.content['main.tf'].outputs = { ...solution.content['main.tf'].outputs, ...blankSolution.content['main.tf'].outputs };
            } else {
                solution.content['main.tf'].outputs = blankSolution.content['main.tf'].outputs;
            }
        }

        this.setSolution(solution);
    }

    addVariable(solution: any, name: string, description: string, type: string, defaultValue: any) {
        solution.content["variables.tf"].variable[name] =  {
            "description": description,
            "type": type,
            "default": defaultValue
        };
        return solution;
    }

    addModuleOutputsToSolution(solution: any, moduleName: string, buildingBlockDefinition: any) {
        if (buildingBlockDefinition.solutionOutput && buildingBlockDefinition.solutionOutput[this.solutionType]) {
            buildingBlockDefinition.solutionOutput[this.solutionType].forEach((output: any) => {
                const outputValue = "${module." + moduleName + "." + output.moduleOutputName + "}";
                if (output.endpointOutput) {
                    // For endpoint outputs, only add it if this type doesn't already exist as an endpoint.
                    const endpoint = this.findEndpointByType(solution, output.type);
                    if (!endpoint) {
                        this.addUrlOutput(solution, {
                            displayName: output.displayName,
                            type: output.type,
                            url: outputValue
                        });
                    }
                } else {
                    solution = this.addOutput(solution, output.outputName, outputValue, output.outputDescription, false);
                }
            });
        }
    }

    addOutput(solution: any, name: string, value: string, description: string, override = true) {
        if (!solution.content["main.tf"].hasOwnProperty('output')) {
            solution.content["main.tf"].output = {};
        }

        if (this.solutionType === "sap-managed" && name === 'subscription_guid') {
            name = 'subscriptionId';
        }

        if (!solution.content["main.tf"].output[name] || override) {
            solution.content["main.tf"].output[name] = {
                "description": description,
                "value": value,
            };
        }

        return solution;
    }

    private ensureEndpointsArray(solution: any): any[] {
        if (!solution.content['main.tf'].output) {
            solution.content['main.tf'].output = {};
        }
        if (!solution.content['main.tf'].output.endpoints) {
            solution.content['main.tf'].output.endpoints = { value: [] };
        }
        if (!Array.isArray(solution.content['main.tf'].output.endpoints.value)) {
            solution.content['main.tf'].output.endpoints.value = [];
        }
        return solution.content['main.tf'].output.endpoints.value;
    }

    findEndpointByType(solution: any, endpointType: string): any {
        const endpoints = solution.content?.['main.tf']?.output?.endpoints?.value;
        if (!Array.isArray(endpoints)) return undefined;
        return endpoints.find((endpoint: any) => endpoint['type'] === endpointType);
    }

    private removeEndpointByType(solution: any, endpointType: string) {
        const endpoints = solution.content?.['main.tf']?.output?.endpoints?.value;
        if (!Array.isArray(endpoints)) return;
        const idx = endpoints.findIndex((endpoint: any) => endpoint['type'] === endpointType);
        if (idx !== -1) {
            endpoints.splice(idx, 1);
        }
        if (endpoints.length === 0) {
            delete solution.content['main.tf'].output.endpoints;
        }
    }

    addUrlOutput(solution: any, urlOutput: any, originalType?: string) {
        const endpointsArray = this.ensureEndpointsArray(solution);
        const typeToFind = originalType || urlOutput.type;
        const existing = this.findEndpointByType(solution, typeToFind);

        if (existing) {
            // Update all properties of existing endpoint
            existing['displayName'] = urlOutput.displayName;
            existing['type'] = urlOutput.type;
            existing['url'] = urlOutput.url;
        } else {
            // Create new endpoint
            endpointsArray.push({
                'displayName': urlOutput.displayName,
                'type': urlOutput.type,
                'url': urlOutput.url
            });
        }

        return solution;
    }

    setLocal(local: string, value: string) {
        let solution = this.getSolution();
        if (solution.content['main.tf'].locals && Array.isArray(solution.content['main.tf'].locals)) {
            solution.content['main.tf'].locals[0][local] = value;
            this.setSolution(solution);
        }
    }

    getAmountOfLocals(): number {
        try {
            return this.getSolution() != null ? Object.keys(this.getSolution()['content']['main.tf']['locals'][0]).length : 0;
        } catch (e) {
            return 0;
        }
    }

    getAmountOfVariables(): number {
        try {
            return this.getSolution() != null ? Object.keys(this.getSolution()['content']['variables.tf']['variable']).length : 0;
        } catch (e) {
            return 0;
        }
    }

    getAmountOfOutputs(): number {
        try {
            return this.getSolution() != null ? Object.keys(this.getSolution()['content']['main.tf']['output']).filter(key => key !== 'configurationUrl').length : 0;
        }catch (e){
            return 0;
        }
    }

    getAmountOfOutputsForEntity(buildingBlock: any): number {
        return this.getBuildingBlocksBySource(buildingBlock.module).length * Math.max(1, buildingBlock.outputs.length);
    }

    getAmountOfOutputsForEntityName(buildingBlock: any) {
        return buildingBlock.outputs;
    }

    getAmountOfVariablesForEntityName(buildingBlock: any) {
        return buildingBlock.variables;
    }

    addDependency(resourceName: string, dependency: string) {
        const solution = this.getSolution();
        const modules = solution.content['main.tf'].module;
        Object.keys(modules).forEach((name: string) => {
            if (name === resourceName) {
                if (!modules[name].hasOwnProperty('dependencies')) {
                    modules[name]['dependencies'] = ["${module." + dependency + "}"];
                } else {
                    modules[name]['dependencies'].push("${module." + dependency + "}");
                }
            }
        });

        this.setSolution(solution);
    }

    removeDependency(resourceName: string, dependencyToRemove: string) {
        const solution = this.getSolution();
        const modules = solution.content['main.tf'].module;
        Object.keys(modules).forEach((name: string) => {
            if (name === resourceName) {
                modules[name]['dependencies'] = modules[name]['dependencies'].filter((dep: string) => dep !== "${module." + dependencyToRemove + "}");
            }
        });

        this.setSolution(solution);
    }

    getDependencies(resourceName: string): any {
        let result: any = [];
        const modules = this.getSolution().content['main.tf'].module;
        Object.keys(modules).forEach((name: string) => {
            if (name === resourceName) {
                if (modules[name].hasOwnProperty('dependencies')) {
                    result = modules[name]["dependencies"];
                }
            }
        });

        return result;
    }

    extractVariablesAndOutputs(moduleName: string) {
        const variables: any[] = [];
        const outputs: any[] = [];
        let providers: string[] = [];
        this.extractSolutionOutputs(this.solutionOutputMetadata);
        this.modulesMetadata?.modules_metadata?.forEach((moduleMetadata: any) => {
            if (moduleMetadata.name === moduleName) {
                this.extractModuleVariables(moduleMetadata, variables);
                this.extractModuleOutputs(moduleMetadata, outputs, moduleName);
                providers = Array.from(Object.keys(moduleMetadata.metadata.required_providers));
            }
        });

        return { providers, variables, outputs };
    }

    private extractSolutionOutputs(solutionOutputMetadata: any): void {
        if (this.solutionOutputs.length > 0) {
            return; // If it's already populated, do nothing
        }

        Object.keys(solutionOutputMetadata).forEach((solutionType) => {
            solutionOutputMetadata[solutionType].forEach((solutionOutput: any) => {
                // Check if the variable already exists in solutionOutputMetadata
                let existingVariable = this.solutionOutputs.find(output =>
                    output.name ==solutionOutput.name);

                if (existingVariable) {
                    existingVariable.solutionTypes.push(solutionType);
                } else {
                    this.solutionOutputs.push({
                        name: solutionOutput.name,
                        description: solutionOutput.description,
                        type: solutionOutput.type || 'string',
                        showButton: false,
                        value: solutionOutput.default_value || '',
                        solutionTypes: [solutionType]
                    });
                }
            });
        });
    }

    private extractModuleOutputs(moduleMetadata: any, outputs: any[], moduleName: string) {
        Array.from(Object.keys(moduleMetadata.metadata.outputs)).forEach(variableName => {
            const type = moduleMetadata.metadata.outputs[variableName].hasOwnProperty('type') === null ? 'string' : moduleMetadata.metadata.outputs[variableName].type;
            outputs.push({
                name: variableName,
                type: type ? type : 'string',
                required: moduleMetadata.metadata.outputs[variableName].required === true,
                entity: moduleName
            });
        });
    }

    private extractModuleVariables(moduleMetadata: any, variables: any[]) {
        Array.from(Object.keys(moduleMetadata.metadata.variables)).forEach(variableName => {
            if (!["dependencies", "subaccount_credentials", "automation_context", "force_delete", "sap_managed_type", "is_saas_cockpit"].includes(variableName)) {
                variables.push({
                    name: variableName,
                    type: moduleMetadata.metadata.variables[variableName].type,
                    required: moduleMetadata.metadata.variables[variableName].required,
                    default: moduleMetadata.metadata.variables[variableName].default
                });
            }
        });
    }

    modulePropertyToTitleCase(s: string) {
        return s
            .replace(/_+/g, ' ')
            .replace("Cis", "CIS")
            .replace("Xsuaa", "XSUAA")
            .replace("Id", "ID")
            .replace("Guid", "GUID")
            .replace("Url", "URL")
            .replace("Oidc", "OIDC")
            .replace("Saas", "SaaS")
            .replace("Ias", "IaS")
            .replace("IDentifier", "Identifier")
            .replace("Crm", "CRM")
            .replace("Scim", "SCIM")
            .replace("Ias", "IAS")
            .replace(/\b\w/g, match => match.toUpperCase())
            .replace("Oauth", "oAuth")
            .replace(/\bauto assign\b/gi, 'Auto-Assign')
            .replace(/\bUser Group Name\b/, 'Group Name');
    }

    generateRandomString(length: number): string {
        const characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        const charactersLength = characters.length;

        for (let i = 0; i < length; i++) {
            const randomIndex = Math.floor(Math.random() * charactersLength);
            result += characters.charAt(randomIndex);
        }

        return result;
    }

    removeEmptyStringKeys(obj: any) {
        for (let key in obj) {
            if (this.keysNotToBeRemoved.includes(key)) {
                continue;
            }
            if (obj[key] === '' || (Array.isArray(obj[key]) && obj[key].length === 0)) {
                delete obj[key];
            } else if (typeof obj[key] === 'object') {
                this.removeEmptyStringKeys(obj[key]);
            }
        }
        return obj;
    }

    async downloadTemplateUCA(solutionSettings: any) {
        let solution = this.getSolution();

        let path = solutionSettings['path'];
        let securedParametersSecretName = solutionSettings['securedParametersSecretName'];

        let template_clone = {
            "apiVersion": "cloud-automation.resource.api.sap/v1",
            "type": "AutomationSolution",
            "metadata": {
                "name": solution.name,
                "path": path
            },
            "spec": {
                "cloudAutomationSecretName": securedParametersSecretName ? securedParametersSecretName :  "cloud-automation-secret",
                "solution": {}
            }
        };

        if (solutionSettings['chinaSecretName']) {
            // @ts-ignore
            template_clone["spec"]["cloudAutomationChinaSecretName"] = solutionSettings['chinaSecretName'];
        }

        let patchByDeletionSolution = this.getPatchByDeletionSolution();
        if (this.solutionType === 'sap-managed' && patchByDeletionSolution) {
            // @ts-ignore
            template_clone['spec']["patchByDeletionSolution"] = {};

            // Copy/override all variables from the solution to the patchByDeletionSolution
            patchByDeletionSolution.content['variables.tf'].variable = { ...solution.content['variables.tf'].variable };

            // Copy/override all locals from the solution to the patchByDeletionSolution
            patchByDeletionSolution.content['main.tf']['locals'][0] = { ...solution.content['main.tf']['locals'][0] };

            this.setSolutionInTemplate(template_clone, patchByDeletionSolution, "patchByDeletionSolution", securedParametersSecretName, solutionSettings['chinaSecretName']);
        }

        this.setSolutionInTemplate(template_clone, solution, "solution", securedParametersSecretName, solutionSettings['chinaSecretName']);

        let spfiNotificationTrustDetails = localStorage.getItem("spfiNotificationTrustDetails");
        if (spfiNotificationTrustDetails) {
            // @ts-ignore
            template_clone['spec']["spfiNotificationTrustDetails"] = JSON.parse(spfiNotificationTrustDetails);
        }

        let solutionParameters = localStorage.getItem("solutionParameters");
        if (solutionParameters) {
            // @ts-ignore
            template_clone['spec']['solution']['parameters'] = JSON.parse(solutionParameters);
        }

        if (this.solutionType === "sap-managed") {
            await this.downloadTemplateSAPManaged(solutionSettings, template_clone);
        } else {
            // @ts-ignore
            template_clone['spec']["sharedSolution"] = solutionSettings['sharedSolutionStatus'];
        }

        let resource = yaml.load(yaml.dump(template_clone).replace(/\\"/g, '"').replace(/display_name:/g, "displayName:").replace(/support_info:/g, "supportInfo:"));
        this.validateBeforeSave(resource);

        return resource;
    }

    setSolutionInTemplate(template_clone: any, solution: any, pathInSpec: string, securedParametersSecretName: string, chinaSecretName?: string) {
        if (this.solutionType === "sap-managed") {
            if (this.getBuildingBlocksBySourceAndSolution(this.buildingBlocksDefinition.trust_setup.module, solution).length === 0 &&
                this.getBuildingBlocksBySourceAndSolution(this.buildingBlocksDefinition.ias_user_group.module, solution).length === 0 &&
                this.getBuildingBlocksBySourceAndSolution(this.buildingBlocksDefinition.ias_app_specific_group.module, solution).length === 0) {
                delete solution.content["main.tf"].locals?.[0]?.['ias_tenant_host'];
            }
        }

        // @ts-ignore
        template_clone['spec'][pathInSpec]['cloudAutomationSolution'] = this.prepareObj(solution);

        if (securedParametersSecretName) {
            // @ts-ignore
            template_clone['spec'][pathInSpec]['securedParametersSecretName'] = securedParametersSecretName;
        }

        if (chinaSecretName) {
            // @ts-ignore
            template_clone['spec'][pathInSpec]['securedParametersChinaSecretName'] = chinaSecretName;
        }
    }

    private async downloadTemplateSAPManaged(solutionSettings: any, template_clone: any) {
        let addDeprovisioning = solutionSettings['deprovisioningStatus'];
        let addSuspension = solutionSettings['suspensionStatus'];
        let addSSOSetup = solutionSettings['ssoSetupStatus'];

        let triggeredBySpfi = localStorage.getItem("triggeredBySpfi");
        if (triggeredBySpfi === "false") {
            // @ts-ignore
            template_clone['spec']["triggeredBySpfi"] = false;
        } else {
            let spfiProvider = localStorage.getItem("spfiProvider");
            if (spfiProvider) {
                // @ts-ignore
                template_clone['spec']["spfiProvider"] = spfiProvider;
            }
        }

        let euporieSecretName = localStorage.getItem("euporieSecretName");
        if (euporieSecretName) {
            template_clone['spec']["euporieSecretName"] = euporieSecretName;
        }

        if (!template_clone['spec']['solution']['parameters'] &&
            (localStorage.getItem("parameters.domain")
             || localStorage.getItem("parameters.cockpit_domain")
             || localStorage.getItem("parameters.global_account_guid")
             || localStorage.getItem("parameters.global_account_subdomain"))) {
            template_clone['spec']['solution']['parameters'] = {};
        }

        if (localStorage.getItem("parameters.domain")) {
            template_clone['spec']['solution']['parameters']['domain'] = localStorage.getItem("parameters.domain");
        }
        if (localStorage.getItem("parameters.cockpit_domain")) {
            template_clone['spec']['solution']['parameters']['cockpit_domain'] = localStorage.getItem("parameters.cockpit_domain");
        }
        if (localStorage.getItem("parameters.global_account_guid")) {
            template_clone['spec']['solution']['parameters']['global_account_guid'] = localStorage.getItem("parameters.global_account_guid");
        }
        if (localStorage.getItem("parameters.global_account_subdomain")) {
            template_clone['spec']['solution']['parameters']['global_account_subdomain'] = localStorage.getItem("parameters.global_account_subdomain");
        }

        if (addDeprovisioning) {
            template_clone['spec']['deprovisioningSolution'] = {
                "useDefault": true
            };
        }

        if (addSuspension) {
            template_clone['spec']['suspensionSolution'] = {
                "useDefault": true
            };
        }

        if (addSSOSetup) {
            template_clone['spec']['ssoSetupSolution'] = {
                "useDefault": true
            };
        }
    }

    private validateBeforeSave(resource:any) {
        this.hclValidation(resource.spec.solution.cloudAutomationSolution?.content?.["main.tf"], "Solution is invalid. Please contact the administrator.");

        if (resource.spec.patchByDeletionSolution) {
            this.hclValidation(resource.spec.patchByDeletionSolution.cloudAutomationSolution?.content?.["main.tf"], "Solution is invalid. Please contact the administrator.");
        }

        let hclParsedVariables = hclParser.parse(this.formatVariablesBeforeHclParsing(resource.spec.solution.cloudAutomationSolution?.content?.["variables.tf"]));
        this.hclValidation(hclParsedVariables, "Solution variables are invalid. Please contact the administrator.");
    }

    private hclValidation(content: any, errorMessage: string=undefined) {
        if (content != undefined) {
            let hclParsedSolution = hclParser.parse(content);
            if (typeof hclParsedSolution === 'string' &&
                (hclParsedSolution.trim().startsWith('unable to parse JSON:') ||
                    hclParsedSolution.trim().startsWith('unable to print JSON:'))) {
                console.error(hclParsedSolution);
                console.error("Invalid solution: ", content);
                throw errorMessage ? errorMessage : hclParsedSolution;
            } else {
                return hclParsedSolution;
            }
        }
    }

    prepareObj(template: any) {
        // Extract endpoints before removeEmptyStringKeys strips empty url values
        let endpointsValue: any[] = null;
        if (template.content['main.tf'].output?.endpoints?.value) {
            endpointsValue = template.content['main.tf'].output.endpoints.value;
            delete template.content['main.tf'].output.endpoints;
        }

        template.content["main.tf"] = this.removeEmptyStringKeys(template.content["main.tf"]);

        const orderedMainTf: any = {};
        if (template.content["main.tf"].locals) {
            orderedMainTf.locals = template.content["main.tf"].locals;
        }
        Object.keys(template.content["main.tf"]).forEach(key => {
            if (key !== 'locals') {
                orderedMainTf[key] = template.content["main.tf"][key];
            }
        });
        const main_tf = hclParser.stringify(JSON.stringify(this.removeEmptyStringKeys(orderedMainTf))).replace(/^\s*[\r\n]/gm,'');
        const variables_tf = hclParser.stringify(JSON.stringify(template.content["variables.tf"])).replace(/^\s*[\r\n]/gm,'');
        const template_clone = JSON.parse(JSON.stringify(template));
        template_clone.content["main.tf"] = main_tf
            .replace(/"([^"]+)" =(.*)/gm, '$1 =$2')
            .replace(/locals =/g, "locals")
            .replace(/\\"\${/g, '"${')
            .replace(/}\\"/g, '}"')
            .replace(/"resource"/g, 'resource')
            .replace(/"module"/g, 'module')
            .replace(/"output"/g, 'output')
            .replace(/module.var/g, 'var')
            .replace(/"description"/g, 'description')
            .replace(/"type"/g, 'type')
            .replace(/"value"/g, 'value')
            .replace("terraform = {}", "terraform {}")
            .replace("output = {}", "")
            .replace(/\\n/g, '\n')
            .replace(/\\\\"/g, '\\"');

        if (template_clone.content["main.tf"].includes("random_uuid")) {
            template_clone.content["main.tf"] += '\nresource "random_uuid" "guid" {}';
        }

        // hack for roles
        const regex = /roles\s*=\s*{([\s\S]*?)}(?!])/g;
        let match;
        while ((match = regex.exec(template_clone.content["main.tf"])) !== null) {
            if (match.length === 1) {
                template_clone.content["main.tf"] = template_clone.content["main.tf"].replace(match[0], `roles = [{${match[0]}}]`);
            }
        }

        // Append endpoints block as manually-built HCL
        if (endpointsValue && endpointsValue.length > 0) {
            template_clone.content["main.tf"] += '\n' + this.buildEndpointsHcl(endpointsValue);
        }

        template_clone.content['variables.tf'] = variables_tf
            .replace(/"(type)" = "([^"]+)"/gm, '$1 = $2')
            .replace(/"variable"/g, 'variable')
            .replace(/"description"/g, 'description')
            .replace(/"default"/g, 'default');

        // Restore endpoints on the original template to avoid side effects
        if (endpointsValue !== null) {
            if (!template.content['main.tf'].output) {
                template.content['main.tf'].output = {};
            }
            template.content['main.tf'].output.endpoints = { value: endpointsValue };
        }

        return template_clone;
    }

    private buildEndpointsHcl(endpoints: any[]): string {
        const entries = endpoints.map(endpoint => {
            const displayName = endpoint['displayName'] || '';
            const typeName = endpoint['type'] || '';
            const url = endpoint['url'] || '';
            return `      {\n        "displayName" = "${displayName}"\n        "type" = "${typeName}"\n        "url" = "${url}"\n      }`;
        });
        return `output "endpoints" {\n    value = [\n${entries.join(',\n')}\n    ]\n}`;
    }

    getSolutionOutputs(): OutputVariable[]{
        return this.solutionOutputs;
    }

    loadSolutionStr(solutionStr: string, resetBeforeLoad: boolean) {
        let solution: any = yaml.load(solutionStr.replace(/UUIDGEN/g, this.generateRandomString(4)));
        this.loadSolution(solution, resetBeforeLoad);
    }

    loadSolutionForEdit(templateSolutionStr: string) {
        let solution: any = JSON.parse(localStorage.getItem("solution"));
        let templateSolution: any = yaml.load(templateSolutionStr);
        templateSolution.description = solution.description;
        templateSolution.displayName = solution.displayName;
        templateSolution.supportInfo = solution.supportInfo;
        templateSolution.name = solution.name;
        this.loadSolution(templateSolution, false);
    }

    isEmptySolution(solution: any) {
        try {
            this.emptySolutionStatus = !solution || Object.keys(solution['content']).length === 0 || Object.keys(solution['content']['main.tf']).length === 0 || solution['content']['main.tf'] === "";
        } catch (e) {
            this.emptySolutionStatus = false;
        }

        return this.emptySolutionStatus;
    }

    isEmptySolutionStatus() :boolean {
        return this.emptySolutionStatus;
    }

    loadSolution(resourceSolution: any, resetBeforeLoad: boolean, store = true) {
        if (resetBeforeLoad) {
            this.clearAll();
        }

        let solution = JSON.parse(JSON.stringify(resourceSolution));

        let deprovioningSolution = null;
        if (this.isUCSResource(solution)) {
            this.handleUCAResource(solution);

            deprovioningSolution = solution['spec']['patchByDeletionSolution']?.['cloudAutomationSolution'];
            if (!this.isEmptySolution(deprovioningSolution)) {
                deprovioningSolution = this.handleSolution(deprovioningSolution);
            }

            solution = solution['spec']['solution']['cloudAutomationSolution'];
        }

        if (this.isEmptySolution(solution)) {
            this.setSolution(solution);
            console.info("Empty solution loaded");
            return;
        }

        solution = this.handleSolution(solution);

        let solutionType = "sap-managed";
        if (JSON.stringify(solution.content["main.tf"]).includes("customer-managed")) {
            solutionType = "customer-managed";
        }

        if (store) {
            const addedBlocks = this.buildAddedBlocks(solution, solutionType);
            localStorage.setItem("addedBlocks", JSON.stringify(addedBlocks));

            this.setSolution(solution);

            if (deprovioningSolution) {
                this.setPatchByDeletionSolution(deprovioningSolution);
            }
        }

        return solution;
    }

    private isUCSResource(solution: any) {
        return solution.hasOwnProperty("apiVersion") && solution.apiVersion === "cloud-automation.resource.api.sap/v1";
    }

    protected handleSolution(solution: any) {
        solution = this.handleSolutionMetadata(solution);

        solution = this.handleMain(solution);

        solution = this.handleLocals(solution);

        solution = this.handleOutput(solution);

        return this.handleVariables(solution);
    }

    public handleSolutionMetadata(solution: any) {
        if (!solution.hasOwnProperty('name')) {
            let randomString = this.generateRandomString(6);
            solution.name = "default-solution-" + randomString;
            if (!solution.hasOwnProperty('displayName')) {
                solution.displayName = "Default Solution " + randomString;
            }
            if (!solution.hasOwnProperty('description')) {
                solution.description = "Default Solution";
            }
            if (!solution.hasOwnProperty('supportInfo')) {
                solution.supportInfo = "For issues and support contact xyz.xyz@sap.com";
            }
        }

        return solution;
    }

    private handleMain(solution: any) {
        if (typeof solution.content["main.tf"] === 'string') {
            let hclParsedMaintf = this.hclValidation(solution.content["main.tf"]);
            solution.content["main.tf"] = JSON.parse(hclParsedMaintf);
        }

        delete solution.content["main.tf"].resource;
        const modulesClone = JSON.parse(JSON.stringify(solution.content["main.tf"].module));
        solution.content["main.tf"].module = {};
        Object.keys(modulesClone).forEach((module: string) => {
            const name = Object.keys(modulesClone[module])[0];
            solution.content["main.tf"].module[name] = modulesClone[module][name][0];
            solution.content["main.tf"].module[name] = this.enrichMissingFields(solution.content["main.tf"].module[name]);
            solution.content["main.tf"].module[name] = this.fixListFields(solution.content["main.tf"].module[name]);
        });

        return solution;
    }

    public handleLocals(solution: any) {
        if (solution.content["main.tf"].locals) {
            const mergedLocals: any = {};
            solution.content["main.tf"].locals.forEach((localBlock: any) => {
                Object.keys(localBlock).forEach((name: string) => {
                    mergedLocals[name] = localBlock[name];
                });
            });
            solution.content["main.tf"].locals = [mergedLocals];
        }
        return solution;
    }

    private handleOutput(solution: any) {
        const mainTf = solution.content["main.tf"];

        if (!mainTf.hasOwnProperty('output')) {
            mainTf['output'] = {};
        } else {
            const outputClone = {...mainTf.output};
            mainTf.output = {};

            // Populate the output object with the updated values
            Object.keys(outputClone).forEach((output) => {
                const name = Object.keys(outputClone[output])[0];
                mainTf.output[name] = outputClone[output][name][0];
            });

            // Restore endpoints structure: HCL parser wraps value in nested arrays
            if (mainTf.output.endpoints?.value) {
                let val = mainTf.output.endpoints.value;
                // HCL parser may wrap in extra arrays — unwrap until we get array of objects
                while (Array.isArray(val) && val.length > 0 && Array.isArray(val[0])) {
                    val = val[0];
                }
                mainTf.output.endpoints = {value: val};
            }


            if (this.solutionType === "sap-managed") {
                // Migration: move legacy outputs into endpoint output
                const isSaasCockpitEnabled = solution?.content?.["main.tf"]?.module?.["subaccount"]?.is_saas_cockpit;
                const legacyOutputs = [
                    {name: 'subscriptionUrl', type: 'application', displayName: 'Main Application URL'}
                ];
                if (isSaasCockpitEnabled === true || isSaasCockpitEnabled === "true"){
                    legacyOutputs.push({name: 'configurationUrl', type: 'configuration', displayName: 'Admin Configuration URL'});
                }
                else {
                    if (mainTf.output?.["configurationUrl"]) {
                        delete mainTf.output["configurationUrl"];
                    }
                    const existingEndpoint = this.findEndpointByType(solution, "configuration");
                    if (existingEndpoint){
                        this.removeEndpointByType(solution, "configuration");
                    }
                }
                legacyOutputs.forEach(legacyOutput => {
                    if (mainTf.output[legacyOutput.name]) {
                        const legacyValue = mainTf.output[legacyOutput.name].value;
                        const existingEndpoint = this.findEndpointByType(solution, legacyOutput.type);
                        if (!existingEndpoint) {
                            const endpointsArray = this.ensureEndpointsArray(solution);
                            endpointsArray.push({
                                'displayName': legacyOutput.displayName,
                                'type': legacyOutput.type,
                                'url': legacyValue
                            });
                        } else if (!existingEndpoint['url'] && legacyValue) {
                            // Fill empty endpoint URL from legacy output
                            existingEndpoint['url'] = legacyValue;
                        }
                        delete mainTf.output[legacyOutput.name];
                    }
                });
            }
        }

        return solution;
    }


    private handleVariables(solution: any) {
        if (typeof solution.content["variables.tf"] === 'string') {
            let hclParsedVariables = this.hclValidation(this.formatVariablesBeforeHclParsing(solution.content["variables.tf"]));
            solution.content["variables.tf"] = JSON.parse(hclParsedVariables);
        }

        const variablesClone = JSON.parse(JSON.stringify(solution.content["variables.tf"].variable));
        solution.content["variables.tf"].variable = {};
        Object.keys(variablesClone).forEach((variable: string) => {
            const name = Object.keys(variablesClone[variable])[0];
            solution.content["variables.tf"].variable[name] = variablesClone[variable][name][0];
        });

        return solution;
    }

    handleUCAResource(solution: any) {
        localStorage.setItem("solutionResource", JSON.stringify(solution));
        localStorage.setItem("solutionPath", solution['metadata']['path']);
        localStorage.setItem("ssn", solution['spec']['cloudAutomationSecretName']);
        if (solution['spec']['cloudAutomationChinaSecretName']) {
            localStorage.setItem("csn", solution['spec']['cloudAutomationChinaSecretName']);
        } else {
            localStorage.removeItem("csn");
        }
        localStorage.setItem("isDeprovision", solution['spec'].hasOwnProperty('deprovisioningSolution'));
        localStorage.setItem("isSSO", solution['spec'].hasOwnProperty('ssoSetupSolution'));

        const triggeredBySpfi = solution['spec']['triggeredBySpfi'] || solution['spec']['spfiProvider'] === 'Euporie' || solution['spec']['spfiProvider'] === 'UCA';
        localStorage.setItem("triggeredBySpfi", triggeredBySpfi);
        if (triggeredBySpfi) {
            const spfiProvider = solution['spec']['spfiProvider'] ? solution['spec']['spfiProvider'] : 'UCA';
            localStorage.setItem("spfiProvider", spfiProvider);
            if (spfiProvider === 'Euporie') {
                localStorage.setItem("euporieSecretName", solution['spec']['euporieSecretName'] ? solution['spec']['euporieSecretName'] : "euporie-secret");
            }
        } else if (triggeredBySpfi === false) {
            localStorage.setItem("triggeredBySpfi", "false"); // if this flag was explicitely set to false, don't override it on save
        }

        if (solution['spec'].hasOwnProperty('sharedSolution')) {
            localStorage.setItem("isSharedSolution", solution['spec']['sharedSolution']);
        }

        if (solution['spec']['spfiNotificationTrustDetails'] !== undefined) {
            let spfiNotificationTrustDetails = JSON.stringify(solution['spec']['spfiNotificationTrustDetails']);
            localStorage.setItem("spfiNotificationTrustDetails", spfiNotificationTrustDetails);
        }

        if (solution['spec']['solution'] !== undefined && solution['spec']['solution']['parameters'] !== undefined) {
            let solutionParameters = JSON.stringify(solution['spec']['solution']['parameters']);
            localStorage.setItem("solutionParameters", solutionParameters);
        }

        if (solution['spec'].hasOwnProperty('ssoSetupSolution')) {
            let regex = /origin_key\s+=\s+\\"(.+?)\\"/;
            let match = JSON.stringify(solution['spec']['ssoSetupSolution']).match(regex);
            let originKey = match ? match[1] : null;
            localStorage.setItem("ssoOriginKey", originKey);
        }

        if (solution['spec']['solution'].hasOwnProperty('parameters')) {
            if (solution['spec']['solution']['parameters'].hasOwnProperty("domain")) {
                localStorage.setItem("parameters.domain", solution['spec']['solution']['parameters']['domain']);
            }
            if (solution['spec']['solution']['parameters'].hasOwnProperty("cockpit_domain")) {
                localStorage.setItem("parameters.cockpit_domain", solution['spec']['solution']['parameters']['cockpit_domain']);
            }
            if (solution['spec']['solution']['parameters'].hasOwnProperty("global_account_guid")) {
                localStorage.setItem("parameters.global_account_guid", solution['spec']['solution']['parameters']['global_account_guid']);
            }
            if (solution['spec']['solution']['parameters'].hasOwnProperty("global_account_subdomain")) {
                localStorage.setItem("parameters.global_account_subdomain", solution['spec']['solution']['parameters']['global_account_subdomain']);
            }
        }
        if (solution['metadata'].hasOwnProperty('labels') && solution['metadata']['labels'].hasOwnProperty("cloud-automation.resource.api.sap/app-operational-mode")) {
            localStorage.setItem("operationalMode", solution['metadata']['labels']["cloud-automation.resource.api.sap/app-operational-mode"]);
        }
        localStorage.setItem("isSuspension", solution['spec'].hasOwnProperty('suspensionSolution'));
    }

    formatVariablesBeforeHclParsing(variables: any) {
        return variables
            .replace(/(type) = ([a-z]+)/gm, '"$1" = "$2"')
            .replace(/variable /g, '"variable" ')
            .replace(/description /g, '"description" ')
            .replace(/(default) = ([a-zA-Z0-9]+)/gm, '"$1" = "$2"');
    }

    buildAddedBlocks(solution: any, solutionType: string) {
        const addedBlocks: string[] = [];
        const modules = solution.content['main.tf'].module;
        for (const entity of Object.keys(this.buildingBlocksDefinition)) {
            if (entity === "subaccount" || entity === "trust_setup") {
                continue;
            }

            const result: string[] = [];
            Object.keys(modules).forEach((name: string) => {

                const moduleSource = modules[name].source.substring(modules[name].source.lastIndexOf('/') + 1);
                const comapre = this.buildingBlocksDefinition[entity].module.includes(solutionType) ? this.buildingBlocksDefinition[entity].module : solutionType + this.buildingBlocksDefinition[entity].module;
                if (moduleSource === comapre) {
                    result.push(name);
                }
            });

            if (result.length > 0) {
                if (!addedBlocks.includes(entity.toLowerCase())) {
                    addedBlocks.push(entity.toLowerCase());
                }
            }
        }
        return addedBlocks;
    }

    getVariableValueDisplay(buildingBlockName: string, variableName: string) {
        let value = this.getVariableValue(buildingBlockName, variableName);
        return this.displayVariableValue(variableName, value);
    }

    displayVariableValue(variableName: string, value: string) {
        if (!value) {
            value = "";
        } else if (typeof value === "string") {
            if (/^\${.*}$/.test(value)) {
                value = value.replace("${", "");
                value = value.substring(0, value.length - 1);
                value = this.displayJsonValue(variableName, value);
            }
        } else {
            value = JSON.stringify(value);
        }

        return value;
    }

    truncateText(text: string, maxLength: number, position: 'start' | 'end' = 'end'): string {
        if (!text || text.length <= maxLength) return text;

        if (position === 'start') {
            return '...' + text.slice(-maxLength);
        } else {
            return text.slice(0, maxLength) + '...';
        }
    }

    getVariableTemplate(entity: any, name: string) {
        if (entity.definition.variableTemplates && entity.definition.variableTemplates[name]) {
            let template = entity.definition.variableTemplates[name];
            return JSON.stringify(template, null, "\t").replace("UUIDGEN",this.generateRandomString(6));
        } else {
            return "";
        }
    }

    moveModulesFromSolutionToPatchByDeletionSolution(moduleNames: string[]) {
        this.addModulesToPatchByDeletionSolution(moduleNames);
        this.removeModulesFromSolution(moduleNames);
    }

    removeModulesFromSolution(moduleNames: string[]) {
        const solution = this.getSolution();

        moduleNames.forEach((name: string) => {
            this.deleteName(name);

            delete solution.content['main.tf'].module[name];
            this.removeReferencesToModule(solution, name, moduleNames);
            this.removeOutputReferencesToModule(solution, name);
        });

        this.setSolution(solution);
    }

    private removeOutputReferencesToModule(solution: any, name: string) {
        if (solution['content']['main.tf']['output']) {
            Object.keys(solution['content']['main.tf']['output']).forEach(output => {
                if (output === 'endpoints') return;
                if (solution['content']['main.tf']['output'][output].value.includes("${module." + name + ".")) {
                    delete solution['content']['main.tf']['output'][output];
                }
            });

            // Filter endpoints array to remove entries referencing deleted module
            const endpoints = solution['content']['main.tf']['output']?.endpoints?.value;
            if (Array.isArray(endpoints)) {
                const affectedEndpoints = endpoints.filter((endpoint: any) => endpoint['url']?.includes("${module." + name + "."));
                const removedEndpoints: any[] = [];

                affectedEndpoints.forEach((endpoint: any) => {
                    // For application endpoints, try to reassign to another subscription module
                    if (endpoint['type'] === 'application') {
                        const modules = solution['content']['main.tf'].module || {};
                        const subscriptionPattern = this.buildingBlocksDefinition.subscription.module;
                        const remainingSubscriptions = Object.keys(modules)
                            .filter((moduleName: string) => {
                                if (moduleName === name) return false;
                                const moduleSource = modules[moduleName].source?.substring(modules[moduleName].source.lastIndexOf('/') + 1);
                                return moduleSource?.endsWith(subscriptionPattern);
                            });
                        if (remainingSubscriptions.length > 0) {
                            endpoint['url'] = '${module.' + remainingSubscriptions[0] + '.subscription_url}';
                            return;
                        }
                    }
                    removedEndpoints.push(endpoint);
                });

                solution['content']['main.tf']['output'].endpoints.value =
                    endpoints.filter((endpoint: any) => !removedEndpoints.includes(endpoint));
                if (solution['content']['main.tf']['output'].endpoints.value.length === 0) {
                    delete solution['content']['main.tf']['output'].endpoints;
                }
            }
        }
    }

    private removeReferencesToModule(solution: any, name: string, moduleNames: string[]) {
        Object.keys(solution.content['main.tf'].module).forEach(moduleName => {
            if (moduleNames.includes(moduleName)) {
                // Don't remove references from modules that are also being removed
                return;
            }

            const module = solution.content['main.tf'].module[moduleName];
            Object.keys(module).forEach(variable => {
                if (variable === 'source') {
                    return;
                }

                // Remove variables that refer to the module from other modules
                const regex = new RegExp(`\\$\\{.*module\\.${name}\\..*\\}`);
                if (typeof module[variable] === 'string' && regex.test(module[variable])) {
                    solution.content['main.tf'].module[moduleName][variable] = "";
                }

                // Remove module from the dependencies of other modules
                if (variable === 'dependencies' && module[variable] && module[variable].includes("${module." + name + "}")) {
                    const elementToRemove = "${module." + name + "}";
                    const array = module[variable];
                    solution.content['main.tf'].module[moduleName][variable] = array.filter((item: string) => item !== elementToRemove);
                }
            });
        });
    }

    addModulesToPatchByDeletionSolution(moduleNames: string[]) {
        const solution = this.getSolution();
        let patchByDeletionSolution = this.getPatchByDeletionSolution();
        if (!patchByDeletionSolution) {
            patchByDeletionSolution = this.loadEmptySolutionForPatchByDeletionSolution(solution);
        }

        moduleNames.forEach((moduleName: string) => {
            this.recursivelyAddModuleToPatchByDeletionSolution(moduleName, patchByDeletionSolution, solution, false);
        });

        this.setPatchByDeletionSolution(patchByDeletionSolution);
    }

    private recursivelyAddModuleToPatchByDeletionSolution(moduleName: string, patchByDeletionSolution: any, solution:any, addAsReadOnly: boolean, visitedModules: Set<string> = new Set()) {
        // Prevent cyclic dependencies
        if (visitedModules.has(moduleName)) {
            return;
        }
        visitedModules.add(moduleName);

        // Create a copy of the module from solution
        const module = { ...solution.content['main.tf'].module[moduleName] };
        const existingModule = patchByDeletionSolution.content['main.tf'].module[moduleName];

        if (addAsReadOnly) {
            // If the module doesn't exist in patchByDeletionSolution, or it has read_only_mode, add read_only_mode: true, to make sure we don't override this property.
            if (!existingModule || existingModule.hasOwnProperty('read_only_mode')) {
                module['read_only_mode'] = true;
            }
        }

        // Assign the updated module from solution to patchByDeletionSolution
        patchByDeletionSolution.content['main.tf'].module[moduleName] = module;

        // Add defined dependencies
        if (module.hasOwnProperty('dependencies')) {
            module['dependencies'].forEach((dependency: string) => {
                const dependencyModuleName = this.getDependencyModuleName(dependency);
                this.recursivelyAddModuleToPatchByDeletionSolution(dependencyModuleName, patchByDeletionSolution, solution, true, visitedModules);
            });
        }

        // Add non direct dependencies
        Object.keys(module).forEach((moduleProperty: string) => {
            const propertyValue = module[moduleProperty];
            const dependencyModuleName = this.getModuleNameFromVariableValue(propertyValue);
            if (dependencyModuleName) {
                this.recursivelyAddModuleToPatchByDeletionSolution(dependencyModuleName, patchByDeletionSolution, solution, true, visitedModules);
            }
        });
    }

    moveModulesFromPatchByDeletionSolutionToSolution(moduleNames: string[]) {
        moduleNames = this.addModulesFromPatchByDeletionSolutionToSolution(moduleNames);
        this.removeModulesFromPatchByDeletionSolution(moduleNames);
    }

    addModulesFromPatchByDeletionSolutionToSolution(moduleNames: string[]) {
        let solution = this.getSolution();
        const patchByDeletionSolution = this.getPatchByDeletionSolution();
        if (!patchByDeletionSolution) {
            return moduleNames;
        }

        // If a module already exists in the solution, first rename it in the patchByDeletionSolution
        let moduleRenamed = false;
        moduleNames.forEach((moduleName: string, index) => {
            if (solution.content['main.tf'].module[moduleName]) {
                const newName = this.generateUniqueName(moduleName);
                this.renameModuleInSolution(patchByDeletionSolution, moduleName, newName);
                moduleNames[index] = newName;
                moduleRenamed = true;
            }
        });

        if (moduleRenamed) {
            this.setPatchByDeletionSolution(patchByDeletionSolution);
        }

        // Add the modules to the solution
        moduleNames.forEach((moduleName: string) => {
            let module = { ...patchByDeletionSolution.content['main.tf'].module[moduleName] };
            this.clearDependenciesToMissingModules(module, moduleNames, solution);
            solution.content['main.tf'].module[moduleName] = module;

            let buildingBlockDefinition = this.getBuildingBlocksDefinitionBySource(module.source);
            this.addModuleOutputsToSolution(solution, moduleName, buildingBlockDefinition);
        });

        this.setSolution(solution);

        return moduleNames;
    }

    private clearDependenciesToMissingModules(module: any, moduleNames: string[], solution: any) {
        let solutionModules = solution.content['main.tf'].module;

        // Clear dependencies to modules that are not in the solution nor in moduleNames
        if (module.hasOwnProperty('dependencies')) {
            module['dependencies'].forEach((dependency: string) => {
                const dependencyModuleName = this.getDependencyModuleName(dependency);
                if (!moduleNames.includes(dependencyModuleName) && //dependency is not in the list of modules that should be moved to solution
                    !solutionModules[dependencyModuleName]) { //dependency is not in the solution
                    module['dependencies'] = module['dependencies'].filter((dep: string) => dep !== dependency);
                }
            });
        }

        // Clear non direct dependencies
        Object.keys(module).forEach((moduleProperty: string) => {
            const propertyValue = module[moduleProperty];
            const dependencyModuleName = this.getModuleNameFromVariableValue(propertyValue);
            if (dependencyModuleName) {
                if (!moduleNames.includes(dependencyModuleName) && //dependency is not in the list of modules that should be moved to solution
                    !solutionModules[dependencyModuleName]) { //dependency is not in the solution
                    module[moduleProperty] = "";
                }
            }
        });
    }

    removeModulesFromPatchByDeletionSolution(moduleNames: string[]) {
        let patchByDeletionSolution = this.getPatchByDeletionSolution();
        if (!patchByDeletionSolution) {
            return;
        }

        let dependencyTree = this.buildSolutionDependencyTree(patchByDeletionSolution);
        this.recursivelyRemoveModuleFromPatchByDeletionSolution(patchByDeletionSolution, moduleNames, dependencyTree);

        this.setPatchByDeletionSolution(patchByDeletionSolution);
    }

    buildSolutionDependencyTree(solution: any) {
        // This is a key-value pair where the key is the dependency module name and the value is a set of module names that depend on it
        // E.g. dependencyTree = {
        //   "assignment": { "cf_service": {"buildingBlockDefinition": {}}, "subscription": {"buildingBlockDefinition": {}} },
        //   "subaccount": { "assignment": {"buildingBlockDefinition": {}} }
        // }
        const dependencyTree: any = {};

        const addDependencyToTree = (moduleName: string, buildingBlockDefinition: any, dependencyModuleName: string) => {
            if (!dependencyTree[dependencyModuleName]) {
                dependencyTree[dependencyModuleName] = {};
            }
            dependencyTree[dependencyModuleName][moduleName] = {
                "buildingBlockDefinition": buildingBlockDefinition
            };
        };

        Object.keys(solution.content['main.tf'].module).forEach((moduleName: string) => {
            const module = solution.content['main.tf'].module[moduleName];
            if (module.hasOwnProperty('dependencies')) {
                module['dependencies'].forEach((dependency: string) => {
                    const dependencyModuleName = this.getDependencyModuleName(dependency);
                    const buildingBlockDefinition = this.getBuildingBlocksDefinitionBySource(module.source);
                    addDependencyToTree(moduleName, buildingBlockDefinition, dependencyModuleName);
                });
            }

            Object.keys(module).forEach((moduleProperty: string) => {
                const propertyValue = module[moduleProperty];
                const dependencyModuleName = this.getModuleNameFromVariableValue(propertyValue);
                if (dependencyModuleName) {
                    const buildingBlockDefinition = this.getBuildingBlocksDefinitionBySource(module.source);
                    addDependencyToTree(moduleName, buildingBlockDefinition, dependencyModuleName);
                }
            });
        });

        return dependencyTree;
    }

    private recursivelyRemoveModuleFromPatchByDeletionSolution(solution: any, moduleNames: string[], dependencyTree: any) {
        const modules = solution.content['main.tf'].module;
        let toDelete: Set<string> = new Set();
        let toReadOnly: Set<string> = new Set();

        const processDependency = (moduleName: string, dependencyModuleName: string) => {
            const references = dependencyTree[dependencyModuleName];
            if (modules[dependencyModuleName]?.read_only_mode && references?.[moduleName]) {
                toDelete.add(dependencyModuleName);
                delete references[moduleName];
                if (Object.keys(references).length === 0) {
                    collectDependencies(dependencyModuleName);
                }
            }
        };

        const collectDependencies = (moduleName: string) => {
            const module = modules[moduleName];
            if (!module) return;

            if (module.hasOwnProperty('dependencies')) {
                module.dependencies.forEach((dependency: string) => {
                    const dependencyModuleName = this.getDependencyModuleName(dependency);
                    processDependency(moduleName, dependencyModuleName);
                });
            }

            Object.keys(module).forEach((moduleProperty: string) => {
                const propertyValue = module[moduleProperty];
                const dependencyModuleName = this.getModuleNameFromVariableValue(propertyValue);
                if (dependencyModuleName) {
                    processDependency(moduleName, dependencyModuleName);
                }
            });
        };

        moduleNames.forEach((moduleName: string) => {
            toDelete.add(moduleName);
            collectDependencies(moduleName);
        });

        toDelete.forEach(moduleName => {
            // Get the modules that depend on the module to be deleted
            let dependentModules = this.getDependentModules(solution, moduleName, dependencyTree, true);

            // Clear the modules to be deleted from the dependent modules
            toDelete.forEach(value => dependentModules.delete(value));

            // If there are dependent modules to this module that are not in the toDelete list, move this module to the toReadOnly list
            if (dependentModules.size > 0) {
                toReadOnly.add(moduleName);
                toDelete.delete(moduleName);
            }
        });

        const subaccountModuleSource = this.buildingBlocksDefinition.subaccount.module;
        const subaccountModuleName = this.getBuildingBlocksBySource(subaccountModuleSource)[0];

        // Actually delete the modules
        toDelete.forEach(moduleName => {
            if (moduleName === subaccountModuleName) {
                // Don't delete the subaccount module, as it is used by the locals.
                // If only readonly modules remain, the entire solution will be deleted.
                return;
            }
            delete solution.content['main.tf'].module[moduleName];
        });

        // Set modules to read_only_mode
        toReadOnly.forEach(moduleName => {
            solution.content['main.tf'].module[moduleName].read_only_mode = true;
        });
    }

    getDependentModules(solution: any, moduleName: string, dependencyTree: any, includeReadOnlyModules = false): Set<string> {
        const solutionModules = solution.content['main.tf'].module;
        let result: Set<string> = new Set();
        const traverse = (name: string): string => {
            if (dependencyTree[name]) {
                Object.keys(dependencyTree[name]).forEach((dependencyName: string) => {
                    if (includeReadOnlyModules ||
                        solutionModules[dependencyName] && !solutionModules[dependencyName].hasOwnProperty('read_only_mode')) { // The requested module is not a dependency of a read_only_mode module
                        result.add(traverse(dependencyName));
                    }
                });
            }
            return name;
        };

        traverse(moduleName);

        return result;
    }


    private getModuleNameFromVariableValue(variableValue: string) {
        if (typeof variableValue !== 'string') {
            return "";
        }

        const match = variableValue.match(/\$\{.*?module\.([^.]+)\./);
        if (match && match[1]) {
            return match[1];
        } else {
            return "";
        }
    }

    getDependencyModuleName(dependency: string) {
        return dependency.replace("${module.", "").replace("}", "");
    }

    loadEmptySolutionForPatchByDeletionSolution(solution: any) {
        let patchByDeletionSolution = {
            name: solution.name + "-patchbydeletion",
            displayName: solution.displayName + " (patch by deletion)",
            description: solution.description + " (patch by deletion)",
            supportInfo: solution.supportInfo,
            content: {
                "main.tf": {
                    locals: [{}],
                    module: {}
                },
                "variables.tf": {
                    variable: {}
                },
            }
        };

        const subaccountModuleSource = this.buildingBlocksDefinition.subaccount.module;
        let subaccountModule = this.getBuildingBlocksBySource(subaccountModuleSource)[0];
        // @ts-ignore
        patchByDeletionSolution.content['main.tf'].module[subaccountModule] = {
            ...solution.content['main.tf'].module[subaccountModule],
            read_only_mode: true
        };
        return patchByDeletionSolution;
    }


    removeExternalInput(name: string) {
        const solution = this.getSolution();
        delete solution.content['variables.tf'].variable[name];
        const seekForDeletion = "${var." + name + "}";

        Object.keys(solution.content['main.tf'].module).forEach(module => {
            Object.keys(solution.content['main.tf'].module[module]).forEach(variable => {
                if (typeof solution.content['main.tf'].module[module][variable] === 'string' && solution.content['main.tf'].module[module][variable].includes(seekForDeletion)) {
                    solution.content['main.tf'].module[module][variable] = "";
                }
            });
        });

        this.setSolution(solution);
    }

    removeExternalOutput(name: string) {
        const solution = this.getSolution();
        delete solution.content['main.tf'].output[name];
        this.setSolution(solution);

    }

    removeUrlOutput(endpointType: string) {
        const solution = this.getSolution();
        this.removeEndpointByType(solution, endpointType);
        this.setSolution(solution);
    }

    getOutputBindings(resource: string, name: string, solution?: any): any[] {
        const bindings: any[] = [];
        const input = '${module.' + resource + '.' + name + '}';

        if (!solution) {
            solution = this.getSolution();
        }

        Object.keys(solution.content['main.tf'].module).forEach(module => {
            Object.keys(solution.content['main.tf'].module[module]).forEach(variable => {
                if (solution.content['main.tf'].module[module][variable] === input) {
                    bindings.push({ 'entity': module, 'input': variable });
                }
            });
        });

        Object.keys(solution.content['main.tf'].locals[0]).forEach(local => {
            if (solution.content['main.tf'].locals[0][local] === input) {
                bindings.push({ 'entity': 'locals', 'input': local });
            }
        });

        return bindings;
    }

    hasURLOutputs(solution: any): boolean {
        return solution.content['main.tf']?.output?.endpoints?.value && solution.content['main.tf']?.output.endpoints?.value?.length > 0;
    }

    getSubscriptionEndpoint(solution: any): any {
        return this.findEndpointByType(solution, 'application');
    }

    getMainSubscription() {
        const solution = this.getSolution();
        let subscriptionUrl;

        // If sap-managed, get the main subscription from the endpoints output with type='application'
        if (this.solutionType === "sap-managed") {
            const endpoint = this.findEndpointByType(solution, 'application');
            subscriptionUrl = endpoint?.['url'];
        }

        // If customer-managed field name is subscription_url
        if (this.solutionType === "customer-managed") {
            subscriptionUrl = solution.content['main.tf'].output?.['subscription_url']?.['value'];
        }

        if (subscriptionUrl) {
            for (const subscription of this.getBuildingBlocksBySource(this.buildingBlocksDefinition.subscription.module)) {
                if (subscriptionUrl.includes("${module." + subscription + ".subscription_url}")) {
                    return subscription;
                }
            }
        }
        return "";
    }

    setMainSubscription(subscription: string) {
        const solution = this.getSolution();
        const outputValue = "${module." + subscription + ".subscription_url}";

        if (this.solutionType === "sap-managed") {
            const endpoint = this.findEndpointByType(solution, 'application');
            if (endpoint) {
                endpoint['url'] = outputValue;
            } else {
                this.addUrlOutput(solution, {
                    displayName: 'Main Application URL',
                    type: 'application',
                    url: outputValue
                });
            }
        }

        // For customer-managed, also update subscription_app_name regular output
        if (this.solutionType === "customer-managed") {
            solution.content['main.tf'].output['subscription_url']['value'] = outputValue;
            solution.content['main.tf'].output['subscription_app_name']['value'] = "${module." + subscription + ".subscription_app_name}";
        }

        this.setSolution(solution);
    }

    public enrichMissingFields(moduleElement: any) {
        const moduleSource = moduleElement.source;
        Object.keys(this.buildingBlocksDefinition).forEach((definition: string) => {
            if (this.buildingBlocksDefinition[definition].source === moduleSource) {
                this.buildingBlocksDefinition[definition].variables.forEach((variable: any) => {
                    if (!moduleElement.hasOwnProperty(variable.name)) {
                        moduleElement[variable.name] = "";
                    }
                });
            }
        });

        return moduleElement;
    }

    fixListFields(moduleElement: any) {
        Object.keys(moduleElement).forEach((key: string) => {
            if (moduleElement.source.includes("role-collection") && key === 'roles' && typeof moduleElement[key] === 'object') {
                moduleElement[key] = "${" + JSON.stringify(moduleElement[key]) + "}";
            }
        });

        return moduleElement;
    }

    pollSolutionStatus() {
        let solutionResourceStr = localStorage.getItem("solutionResource");
        if (!solutionResourceStr) {
            this.updateSolutionStatus(null, false);
            return;
        }

        let solutionResource = JSON.parse(solutionResourceStr);
        this.updateSolutionStatus(solutionResource, false);

        let fetchUrl = `/api/resources/${solutionResource.apiVersion}/${solutionResource.type}/${solutionResource.metadata?.path}/${solutionResource.metadata?.name}`;

        this.pollingActive = true;
        let noStatusCount = 0;
        this.pollingService.addUrlTask(fetchUrl, (response: FetchResponse) => {
            // Check if polling has been stopped
            if (!this.pollingActive) {
                return true;
            }

            if (!response.ok) {
                console.log('response from server was bad', response.statusText, response.body);
                this.solutionReadiness.solutionReadyStatus = null;
                return true;

            }

            let resource = response.body;
            localStorage.setItem("solutionResource", JSON.stringify(resource));

            let readyCondition = resource.status?.conditions?.find((condition: any) => condition.type === "Ready");
            if (!readyCondition) {
                noStatusCount++;
            }

            const setNoStatus = noStatusCount > this.MAX_POLLING_NO_STATUS;
            this.updateSolutionStatus(resource, setNoStatus);

            if (setNoStatus) {
                // Reached max polling. Stop polling.
                this.pollingActive = false;
                return true;
            } else if (!resource.status) {
                // Has no status. Continue polling.
                return false;
            }
            // if reached a final status, stop polling (return true). If not, continue polling (return false).
            const shouldStop = !this.isResourceInProcess(this.getState(resource, false));
            if (shouldStop) {
                this.pollingActive = false;
            }
            return shouldStop;
        });
    }

    stopSolutionPolling() {
        this.pollingActive = false;
    }

    updateSolutionStatus(resource: any, setUnknown: boolean) {
        if (resource &&
            resource.apiVersion &&
            resource.type &&
            resource.metadata?.path &&
            resource.metadata?.name) {
            this.solutionReadiness.solutionReadyStatus = this.getState(resource, setUnknown);
            this.solutionReadiness.solutionLastModifiedDate = resource.metadata?.modificationTime;
            if (this.solutionReadiness.solutionReadyStatus === ResourceState.NOT_READY) {
                // Set the error message as tooltip
                this.solutionReadiness.solutionReadyStatusTooltip = resource.status?.conditions?.find((condition: any) => condition.type === "Ready")?.message;
            } else {
                // Use the default tooltip
                this.solutionReadiness.solutionReadyStatusTooltip = undefined;
            }
        } else {
            // If there is no resource, clear possible previous status.
            this.solutionReadiness.solutionReadyStatus = undefined;
            this.solutionReadiness.solutionLastModifiedDate = undefined;
            this.solutionReadiness.solutionReadyStatusTooltip = undefined;
        }
    }

    getState(resource: any, setUnknown: boolean) {
        const conditions = resource.status?.conditions;
        if (!conditions) {
            return setUnknown ? ResourceState.UNKNOWN : ResourceState.IN_PROCESS;
        }

        let readyCondition = conditions.find((condition: any) => condition.type === "Ready");
        if (!readyCondition) {
            return setUnknown ? ResourceState.UNKNOWN : ResourceState.IN_PROCESS;
        }

        if (!this.isGenerationUpdated(resource, readyCondition)) {
            return setUnknown ? ResourceState.UNKNOWN : ResourceState.IN_PROCESS;
        }

        if (readyCondition.status === "True") {
            return ResourceState.READY;
        } else {
            return readyCondition.reason === ResourceState.IN_PROCESS ? ResourceState.IN_PROCESS : ResourceState.NOT_READY;
        }
    }

    isGenerationUpdated(resource: any, readyCondition: any) {
        return resource.metadata.generation === readyCondition.observedGeneration;
    }

    isResourceInProcess(state: ResourceState) {
        return (
            !state ||
            state === ResourceState.IN_PROCESS ||
            state === ResourceState.TERMINATING
        );
    }

    isReadOnlyLocal(localKey: string) {
        const solutionType = this.solutionType as keyof typeof this.readOnlyLocals;
        return this.readOnlyLocals[solutionType].includes(localKey);
    }

    getNotReadOnlyLocalNames() {
        return Object.keys(this.getSolution()?.content?.['main.tf']?.locals?.[0] || {}).filter(localKey => !this.isReadOnlyLocal(localKey));
    }

    isSaasCockpitEnabled():boolean {
        let saasCockpitStatus = this.getVariableValue("subaccount", "is_saas_cockpit");
        return saasCockpitStatus === 'true';
    }
}
